// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSubscriptionStore from '@/app/store/modules/subscription';
import actionsSubscriptions from '../../api/actionsSubscriptions';
// Components
import PlansTable from '@/app/modules/plans/components/tables/PlansTable';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsPlan } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.subscriptionId
 * @param root0.allowedClientId
 * @param root0.organizationId
 * @returns {JSX} ModalUpdateSubscriptionWithOrWithoutPlan
 */
function ModalUpdateSubscriptionWithOrWithoutPlan({ isOpen, setIsOpen, subscriptionId, allowedClientId, organizationId }) {
    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.update_subscription_plan.steps';

    // Toast
    const { toast } = useToast();

    // Store
    const refetchFromStore = useSubscriptionStore(s => s.storeRefetchSubscriptions);

    // State
    const [planSelected, setPlanSelected] = useState(getInitialValueForRowSelected());
    const [step, setStep] = useState(0);

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        refetchFromStore(true);
        toast({
            description: <Entity entity={'modal.update_subscription_plan.success'} namespace={nsPlan} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity={'modal.update_subscription_plan.error'} namespace={nsPlan} />,
            variant: 'danger'
        });
    };
    const updateSubscription = actionsSubscriptions.updateSubscription(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.select_plan`,
                disabledNext: !Object.keys(planSelected.rowIds).length
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirm`,
                disabledNext: false
            }
        }),
        [planSelected]
    );

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step === 0 ? 'max-w-6xl' : 'max-w-lg'}>
                <HeaderTitle title={'modal.update_subscription_plan.title'} namespace={nsPlan} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsPlan}
                        className={'mb-2 flex'}
                    />
                    {step === 0 &&
                        <PlansTable
                            filtersEventName={setFiltersEventName('plans', true)}
                            organizationId={organizationId}
                            singleSelection={true}
                            setRowsSelected={setPlanSelected}
                            rowsSelected={planSelected}
                        />
                    }
                    {step === 1 &&
                        <div className='space-y-2'>
                            <div><Entity entity='modal.update_subscription_plan.text' namespace={nsPlan} data={{ planLabel: planSelected.rowsData[0].label }} /></div>
                            <div className='text-center'><Entity entity='modal.update_subscription_plan.confirm' namespace={nsPlan} /></div>
                        </div>
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={
                        () => {
                            updateSubscription.mutate({
                                allowedClientId: allowedClientId,
                                subscriptionId: subscriptionId,
                                planId: planSelected.rowsData[0].uuid
                            });
                        }
                    }
                    isLoading={updateSubscription.isLoading}
                    disableValidation={disabledNext}
                />
            </ModalContent>;
        </Modal>
    );
}

ModalUpdateSubscriptionWithOrWithoutPlan.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    subscriptionId: PropTypes.string,
    allowedClientId: PropTypes.string,
    organizationId: PropTypes.string
};

export default ModalUpdateSubscriptionWithOrWithoutPlan;